import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MonitorPlayIcon, ChartLineIcon } from '@phosphor-icons/react';
import { Chart, ChartDataPoint, ChartSkeleton } from '@/components/Chart';
import { SegmentedControl } from '@/components/SegmentedControl';
import { AnimatedNumber } from '@/components/AnimatedNumber';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import type { DirectoryItemSourceStats } from '../types';
import { stripFileExtension } from '@/utils/file-name';
import {
    PERIOD_CONFIG,
    ANALYTICS_PERIODS,
    formatChartAxisByGroupBy,
    usePeriodOptions,
    useChartTooltipProps,
    type PeriodOption,
    type GroupByOption,
} from '@/utils/chart-periods';

export type { PeriodOption, GroupByOption };

const MAX_SOURCES_DISPLAY = 24;

interface DirectoryItemChartProps {
    chartData: ChartDataPoint[];
    totalEvents: number;
    sourceStats: DirectoryItemSourceStats[];
    isLoading: boolean;
    activePeriod: PeriodOption;
    onPeriodChange: (period: PeriodOption) => void;
    onPointClick?: (dataPoint: ChartDataPoint, index: number) => void;
    onSourceClick?: (source: DirectoryItemSourceStats) => void;
}

export function DirectoryItemChart({
    chartData,
    totalEvents,
    sourceStats,
    isLoading,
    activePeriod,
    onPeriodChange,
    onPointClick,
    onSourceClick,
}: DirectoryItemChartProps) {
    const { t } = useTranslation(I18nNamespace.EVENTS);
    const config = PERIOD_CONFIG[activePeriod];
    const displaySources = useMemo(
        () => sourceStats.slice(0, MAX_SOURCES_DISPLAY),
        [sourceStats],
    );
    const { tooltipSources, tooltipTitleFormatter } = useChartTooltipProps(
        config.groupBy,
        displaySources,
    );

    const periodOptions = usePeriodOptions(t, ANALYTICS_PERIODS);

    const xAxisTickFormatter = useCallback(
        (value: string) => formatChartAxisByGroupBy(value, config.groupBy),
        [config.groupBy],
    );

    return (
        <div className="w-full max-w-[900px] mx-auto text-white-100">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                <div className="flex-1">
                    <div className="text-sm text-grey-200 mb-2">
                        {t('chart.events')}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-white-100">
                        <MonitorPlayIcon className="w-4 h-4 text-grey-200" />
                        <AnimatedNumber
                            key={activePeriod}
                            value={totalEvents}
                        />
                        <span className="text-sm text-white-100">
                            {t(`chart.inLast.${activePeriod}`)}
                        </span>
                    </div>
                </div>

                <SegmentedControl
                    value={activePeriod}
                    onValueChange={onPeriodChange}
                    options={periodOptions}
                />
            </div>

            <div className="mb-12">
                {isLoading && chartData.length === 0 ? (
                    <ChartSkeleton height={298} />
                ) : chartData.length > 0 ? (
                    <Chart
                        data={chartData}
                        dataKey="value"
                        strokeColor="var(--brand)"
                        fillColor="var(--brand)"
                        height={298}
                        tooltipLabel={t('chart.events')}
                        tooltipSources={tooltipSources}
                        tooltipTitleFormatter={tooltipTitleFormatter}
                        onPointClick={onPointClick}
                        xAxisTickFormatter={xAxisTickFormatter}
                        xAxisInterval={config.xAxisInterval}
                        margin={{
                            top: 10,
                            right: 20,
                            left: -20,
                            bottom: config.bottomMargin || 0,
                        }}
                    />
                ) : (
                    <div className="h-[298px] flex flex-col items-center justify-center rounded-lg border border-dashed border-grey-500 bg-grey-700/30">
                        <div className="w-16 h-16 rounded-full bg-grey-600/50 flex items-center justify-center mb-4">
                            <ChartLineIcon className="w-8 h-8 text-grey-400" />
                        </div>
                        <div className="text-sm text-grey-300 mb-1">
                            {t('chart.noData')}
                        </div>
                        <div className="text-xs text-grey-400">
                            {t('chart.noDataDescription')}
                        </div>
                    </div>
                )}
            </div>

            {displaySources.length > 0 && (
                <div className="grid grid-cols-1 @medium:grid-cols-3 gap-x-8 gap-y-4">
                    {displaySources.map((item) => (
                        <div key={item.id} className="flex flex-col gap-2">
                            {onSourceClick ? (
                                <button
                                    type="button"
                                    onClick={() => onSourceClick(item)}
                                    className="text-xs-medium text-grey-200 hover:text-white-100 transition-colors text-left cursor-pointer"
                                >
                                    {stripFileExtension(item.name)}
                                </button>
                            ) : (
                                <span className="text-xs-medium text-grey-200">
                                    {stripFileExtension(item.name)}
                                </span>
                            )}
                            <div className="flex items-center gap-4">
                                <div className="flex-1 h-2.5 bg-grey-800 rounded-sm overflow-hidden">
                                    <div
                                        className="h-full bg-grey-600 rounded-sm transition-all"
                                        style={{
                                            width: `${(item.value / totalEvents) * 100}%`,
                                        }}
                                    />
                                </div>
                                <div className="text-xs text-white-100 min-w-[15px]">
                                    {item.value}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
